/**
 * Reminder and Notification Management Types
 * For granular notification control and engagement tracking
 */

export type DigestMode = 'immediate' | 'daily' | 'weekly';
export type NotificationChannel = 'email' | 'in-app' | 'push';
export type ReminderFrequency = 'one-time' | 'recurring';

/**
 * Global reminder preference for a specific reminder type
 */
export interface ReminderPreference {
  reminderType: string; // e.g., "Rent Increase Due - Date Set"
  enabled: boolean;
  notifyDays: number[]; // Days before due date to send notifications
  emailEnabled: boolean;
  inAppEnabled: boolean;
  digestMode: DigestMode;
  smartSuggestionsEnabled: boolean;
  customMessage?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Property-specific override for a reminder type
 */
export interface PropertyReminderOverride {
  propertyId: string;
  propertyAddress: string;
  reminderType: string;
  enabled: boolean;
  notifyDays: number[];
  emailEnabled: boolean;
  inAppEnabled: boolean;
  frequency: ReminderFrequency;
  customMessage?: string;
  expiresAt?: string; // For one-time overrides
  createdAt: string;
  updatedAt: string;
}

/**
 * Notification history entry for tracking
 */
export interface NotificationHistory {
  id: string;
  userEmail: string;
  propertyId?: string;
  propertyAddress?: string;
  reminderType: string;
  dueDate: string;
  notificationDate: string;
  channel: NotificationChannel;
  sentAt: string;
  openedAt?: string;
  clickedAt?: string;
  dismissedAt?: string;
  isDigest: boolean;
  digestId?: string;
  trackingId: string;
}

/**
 * Digest queue entry for batching notifications
 */
export interface DigestQueueEntry {
  id: string;
  userEmail: string;
  propertyId: string;
  propertyAddress: string;
  reminderType: string;
  dueDate: string;
  daysBefore: number;
  priority: 'low' | 'medium' | 'high';
  queuedAt: string;
  scheduledFor: string; // When digest should be sent
}

/**
 * Smart suggestion for notification optimization
 */
export interface SmartSuggestion {
  id: string;
  userEmail: string;
  type: 'disable-unused' | 'adjust-timing' | 'enable-digest' | 'replicate-pattern';
  reminderType: string;
  currentSettings: Partial<ReminderPreference>;
  suggestedSettings: Partial<ReminderPreference>;
  reason: string;
  impact: string; // e.g., "Reduce notifications by 40%"
  confidence: number; // 0-100
  basedOnData: {
    totalSent: number;
    totalOpened: number;
    openRate: number;
    avgDaysBeforeAction: number;
  };
  createdAt: string;
  status: 'pending' | 'accepted' | 'rejected' | 'expired';
  appliedAt?: string;
}

/**
 * Engagement statistics for a reminder type
 */
export interface EngagementStats {
  reminderType: string;
  totalSent: number;
  totalOpened: number;
  totalClicked: number;
  totalDismissed: number;
  openRate: number;
  clickRate: number;
  avgTimeToOpen: number; // in hours
  avgDaysBeforeAction: number;
  mostEngagedDay: number; // day before due date with highest engagement
  leastEngagedDay: number;
  lastSent?: string;
  lastOpened?: string;
}

/**
 * Notification preset template
 */
export interface NotificationPreset {
  id: string;
  name: string;
  description: string;
  isSystem: boolean; // true for built-in presets
  preferences: Record<string, Partial<ReminderPreference>>;
  createdBy?: string;
  createdAt: string;
}

/**
 * Default notification days by reminder importance
 */
export const DEFAULT_NOTIFY_DAYS: Record<string, number[]> = {
  // Critical compliance dates
  'Fire Safety Certificate Expiry - Date Set': [90, 60, 30, 14, 7],
  'Pool Safety Certificate Expiry - Date Set': [90, 60, 30, 14, 7],
  
  // Important financial dates
  'Loan Review Date - Date Set': [120, 90, 60, 30],
  'Insurance Renewal - Date Set': [60, 30, 14, 7],
  
  // Standard property dates
  'Rent Increase Due - Date Set': [90, 60, 30],
  'Lease Expiry - Date Set': [90, 60, 30],
  'Council Rates Due - Date Set': [60, 30, 14],
  'Water Rates Due - Date Set': [60, 30, 14],
};

/**
 * System presets
 */
export const SYSTEM_PRESETS: NotificationPreset[] = [
  {
    id: 'conservative',
    name: 'Conservative',
    description: 'Maximum reminders with early warnings',
    isSystem: true,
    preferences: {
      'all': {
        enabled: true,
        emailEnabled: true,
        inAppEnabled: true,
        digestMode: 'immediate',
        smartSuggestionsEnabled: false,
      }
    },
    createdAt: new Date().toISOString(),
  },
  {
    id: 'balanced',
    name: 'Balanced',
    description: 'Standard reminders for important events',
    isSystem: true,
    preferences: {
      'all': {
        enabled: true,
        emailEnabled: true,
        inAppEnabled: true,
        digestMode: 'daily',
        smartSuggestionsEnabled: true,
      }
    },
    createdAt: new Date().toISOString(),
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Only critical reminders close to due dates',
    isSystem: true,
    preferences: {
      'all': {
        enabled: true,
        emailEnabled: true,
        inAppEnabled: false,
        digestMode: 'weekly',
        smartSuggestionsEnabled: true,
      }
    },
    createdAt: new Date().toISOString(),
  },
];
